import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable, distinctUntilChanged } from 'rxjs';

export type AdUserDataConsent = 'granted' | 'denied' | 'unknown';

interface UsercentricsServiceInfo {
    name?: unknown;
    consent?: {
        status?: unknown;
    };
}

interface UsercentricsUi {
    isInitialized?: () => boolean;
    getServicesBaseInfo?: () => UsercentricsServiceInfo[];
}

/**
 * Single consent source for tracking. It intentionally does not expose CMP
 * implementation details to tracking services.
 */
@Injectable({ providedIn: 'root' })
export class ConsentService {
    private readonly consentSubject = new BehaviorSubject<AdUserDataConsent>('unknown');
    private mode: 'internal' | 'usercentrics' = 'internal';
    private usercentricsListenersRegistered = false;

    readonly adUserDataConsent$: Observable<AdUserDataConsent> = this.consentSubject
        .asObservable()
        .pipe(distinctUntilChanged());

    constructor(
        @Inject(PLATFORM_ID) private readonly platformId: object,
        @Inject(DOCUMENT) private readonly document: Document,
    ) {}

    hasAdUserDataConsent(): boolean {
        return this.consentSubject.value === 'granted';
    }

    /** Called for the existing first-party cookie dialog flow. */
    useInternalConsent(): void {
        this.mode = 'internal';
    }

    /** Mirrors the same decision that the legacy internal flow sends to gtag. */
    setInternalAdUserDataConsent(consent: 'granted' | 'denied'): void {
        if (this.mode !== 'internal') return;
        this.setConsent(consent);
    }

    /** Called before the existing Usercentrics CMP v2 loader is injected. */
    useUsercentricsConsent(): void {
        this.mode = 'usercentrics';
        this.setConsent('unknown');
        if (!this.isBrowser()) return;

        if (!this.usercentricsListenersRegistered) {
            const win = this.window();
            win.addEventListener('UC_UI_INITIALIZED', this.onUsercentricsReady);
            win.addEventListener('ucEvent', this.onUsercentricsConsentEvent);
            this.usercentricsListenersRegistered = true;
        }

        this.refreshUsercentricsConsent();
    }

    private readonly onUsercentricsReady = (): void => this.refreshUsercentricsConsent();
    private readonly onUsercentricsConsentEvent = (): void => this.refreshUsercentricsConsent();

    private refreshUsercentricsConsent(): void {
        if (this.mode !== 'usercentrics' || !this.isBrowser()) return;

        const ucUi = (this.window() as Window & { UC_UI?: UsercentricsUi }).UC_UI;
        if (!ucUi?.getServicesBaseInfo || (ucUi.isInitialized && !ucUi.isInitialized())) {
            this.setConsent('unknown');
            return;
        }

        try {
            const googleAds = ucUi.getServicesBaseInfo()
                ?.find((service) => service?.name === 'Google Ads');

            if (!googleAds) {
                this.setConsent('unknown');
                return;
            }

            const consent: AdUserDataConsent = googleAds.consent?.status === true ? 'granted' : 'denied';
            this.setConsent(consent);
            this.syncUsercentricsGtagConsent(consent);
        } catch {
            // A loader that is still initializing must never be treated as consent.
            this.setConsent('unknown');
        }
    }

    private syncUsercentricsGtagConsent(consent: 'granted' | 'denied'): void {
        const gtag = (this.window() as any).gtag;
        gtag?.('consent', 'update', { ad_user_data: consent });
    }

    private setConsent(consent: AdUserDataConsent): void {
        if (this.consentSubject.value !== consent) this.consentSubject.next(consent);
    }

    private isBrowser(): boolean {
        return isPlatformBrowser(this.platformId);
    }

    private window(): Window {
        return this.document.defaultView!;
    }
}
